import { AnimatePresence, motion, Variants } from 'framer-motion';
import { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { RichMediaState } from '@bonusly/common-action-input';
import {
	Alert,
	AlertIcon,
	Avatar,
	Box,
	Button,
	ChevronDownOutline,
	ChevronUpOutline,
	DatePicker,
	FormControl,
	FormErrorMessage,
	FormLabel,
	HStack,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Select,
	Stack,
	Tag,
	Text,
	Textarea,
	useBreakpointValue,
	useDisclosure,
	useToast,
	VStack,
	Wrap,
	XOutline,
} from '@bonusly/design-system';
import { Recipient } from '@bonusly/recipient-entities';
import { useUser } from '@bonusly/user-queries';
import { ProductTier, useCurrentCompany, useCurrentUser } from '@bonusly/who-am-i';

import { useFetchRecipients } from '../../../../components/recognition/give-box/hooks/use-fetch-recipients';
import { GoalStatus } from '../../../../types/goal';
import { parseDateSafely } from '../../../../utils/date';
import { useInitiatives } from '../../../admin-growth/hooks/use-initiatives';
import { useHashtags } from '../../../bonus-feed/features/recognition/api/hooks/hashtags';
import { useCreateGoal } from '../../hooks/use-goals';
import { CollaboratorSelector } from './collaborator-selector';
import { GoalInitiativeCard } from './goal-initiative-card';
import { GoalStatusSelect } from './goal-status-select';

const MotionBox = motion.create(Box);
const MotionVStack = motion.create(VStack);

interface FormData {
	title: string;
	summary: string;
	status: GoalStatus;
	startDate: string;
	endDate: string;
	tags: string[];
	collaborators: Recipient[];
	initiativeId: string;
}

interface CreateGoalModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: (goal: any) => void;
	targetUserId?: string; // Optional: if provided, create goal for this user instead of current user
}

// Animation variants for cute transitions
const contentVariants: Variants = {
	hidden: { opacity: 0, x: -30 },
	visible: {
		opacity: 1,
		x: 0,
		transition: {
			type: 'spring',
			stiffness: 300,
			damping: 30,
			staggerChildren: 0.05,
		},
	},
	exit: {
		opacity: 0,
		x: 30,
		transition: { duration: 0.2 },
	},
};

const itemVariants: Variants = {
	hidden: { opacity: 0, y: 15 },
	visible: {
		opacity: 1,
		y: 0,
		transition: {
			type: 'spring',
			stiffness: 400,
			damping: 35,
		},
	},
};

const advancedOptionsVariants: Variants = {
	hidden: {
		opacity: 0,
		height: 0,
		y: -10,
	},
	visible: {
		opacity: 1,
		height: 'auto',
		y: 0,
		transition: {
			type: 'spring',
			stiffness: 300,
			damping: 35,
			opacity: { duration: 0.3 },
			height: { duration: 0.2, ease: 'easeOut' },
			y: { duration: 0.3, ease: 'easeOut' },
		},
	},
	exit: {
		opacity: 0,
		height: 0,
		y: -10,
		transition: {
			duration: 0.3,
			ease: 'easeInOut',
		},
	},
};

export const CreateGoalModal: FC<CreateGoalModalProps> = ({ isOpen, onClose, onSuccess, targetUserId }) => {
	const { isOpen: isAdvancedOpen, onToggle: onAdvancedToggle, onClose: onAdvancedClose } = useDisclosure();
	const toast = useToast();
	const createGoal = useCreateGoal();
	const user = useCurrentUser();
	const company = useCurrentCompany();

	const isOrganizationTier = company.product_tier === ProductTier.TierThree;
	const { data: initiatives = [] } = useInitiatives(isOrganizationTier);

	// Fetch target user info if creating goal for someone else
	const { data: targetUser } = useUser(targetUserId);
	const isCreatingForOther = !!targetUserId && targetUserId !== user.id;

	const bgColor = 'bg.surface';
	const saveButtonText = useBreakpointValue({
		base: isCreatingForOther ? 'Create' : 'Save',
		md: isCreatingForOther ? `Create for ${targetUser?.displayFirstName}` : 'Save goal',
	});

	// Common styles for consistent form field styling
	const labelContainerStyle = {
		justify: 'space-between' as const,
		align: 'center' as const,
		mb: 1,
	};

	const formLabelStyle = {
		fontSize: 'sm',
		fontWeight: 'medium',
		color: 'text.default',
		mb: 0,
	};

	const optionalTextStyle = {
		fontSize: 'sm',
		color: 'text.softest',
	};

	// Reset advanced options to closed whenever modal opens
	useEffect(() => {
		if (isOpen) {
			onAdvancedClose();
		}
	}, [isOpen, onAdvancedClose]);

	// Get company values aka hashtags
	const { data: hashtags } = useHashtags({
		params: {
			display_format: 'index',
		},
	});

	// Real search function using useFetchRecipients hook
	const fetchRecipients = useFetchRecipients({
		include_self: false, // Allow selecting yourself if needed
		include_non_receivable: true, // Include inactive users if needed
		search_usergroups: false, // Keep groups excluded for now
		search_users: true,
	});

	const searchCollaborators = async (query: string): Promise<Recipient[]> => {
		const emptyState = {
			mentions: [],
			recipients: [],
			hashtags: [],
			json: null,
			plainText: '',
			text: '',
		} as unknown as RichMediaState;

		const results = await fetchRecipients({
			query,
			state: emptyState,
		});

		return results;
	};

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors, isSubmitting },
	} = useForm<FormData>({
		defaultValues: {
			title: '',
			summary: '',
			status: 'not_started',
			startDate: '',
			endDate: '',
			tags: [],
			collaborators: [],
			initiativeId: '',
		},
	});

	const handleClose = () => {
		reset();
		onClose();
	};

	const onSubmit = async (data: FormData) => {
		try {
			// Transform collaborators (Recipient objects) to user IDs for the API
			const collaboratorIds = data.collaborators.map((collaborator) => collaborator.id);
			const goalPayload = {
				...data,
				userId: targetUserId || user.id, // Use targetUserId if provided (manager creating for direct report)
				collaborators: collaboratorIds,
			};

			await createGoal.mutateAsync(goalPayload);

			toast({
				title: 'Goal created successfully!',
				status: 'success',
				duration: 3000,
			});

			onSuccess?.(data);
			handleClose();
		} catch (error) {
			console.error('Failed to create goal:', error);
			toast({
				title: 'Failed to create goal',
				description: error instanceof Error ? error.message : 'Unknown error occurred',
				status: 'error',
				duration: 3000,
			});
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={handleClose} size={{ base: 'full', md: 'xl' }}>
			<ModalOverlay />
			<ModalContent
				bg={bgColor}
				borderRadius={{ base: 0, md: 'xl' }}
				border="none"
				maxH={{ base: '100vh', md: 'calc(100vh - 40px)' }}
				display="flex"
				flexDirection="column"
			>
				<ModalHeader pb={3} borderBottom="none">
					<Text fontSize="xl" fontWeight="semibold">
						{isCreatingForOther ? `Create a goal for ${targetUser?.displayFirstName}` : 'Create a new goal'}
					</Text>
				</ModalHeader>
				<ModalCloseButton />

				{/* Banner when creating for someone else */}
				{isCreatingForOther && targetUser && (
					<Box px={{ base: 4, md: 5 }} pt={{ base: 4, md: 5 }}>
						<Alert status="info" borderRadius="md">
							<AlertIcon />
							<HStack spacing={3} flex={1}>
								<Avatar size="sm" name={targetUser.displayName} src={targetUser.profilePic || ''} />
								<VStack spacing={0} align="flex-start">
									<Text fontSize="sm" fontWeight="medium">
										Creating a goal for {targetUser.displayName}
									</Text>
									<Text fontSize="xs" color="text.softer">
										This goal will be assigned to them
									</Text>
								</VStack>
							</HStack>
						</Alert>
					</Box>
				)}

				<ModalBody py={{ base: 4, md: 5 }} px={{ base: 4, md: 5 }} overflowY="auto" flex="1">
					<AnimatePresence mode="wait">
						<MotionVStack key="manual-mode" variants={contentVariants} initial="hidden" animate="visible" exit="exit" spacing={0} align="stretch">
							<form id="create-goal-form" onSubmit={handleSubmit(onSubmit)}>
								<VStack spacing={4} align="stretch">
									{/* Goal Title */}
									<MotionBox variants={itemVariants}>
										<FormControl isInvalid={!!errors.title}>
											<HStack {...labelContainerStyle}>
												<FormLabel {...formLabelStyle}>Goal title</FormLabel>
											</HStack>
											<Controller
												name="title"
												control={control}
												rules={{ required: 'Goal title is required' }}
												render={({ field }) => (
													<Input
														{...field}
														placeholder="Enter goal title"
														size="md"
														borderRadius="md"
														border="1px solid"
														borderColor="border.default"
														_focus={{ borderColor: 'border.focus', boxShadow: '0 0 0 1px var(--chakra-colors-border-focus)' }}
														autoComplete="off"
														data-dashlane-rid="false"
														data-1p-ignore="true"
													/>
												)}
											/>
											<FormErrorMessage>{errors.title?.message}</FormErrorMessage>
										</FormControl>
									</MotionBox>

									{/* Goal Details */}
									<MotionBox variants={itemVariants}>
										<FormControl>
											<HStack {...labelContainerStyle}>
												<FormLabel {...formLabelStyle}>Goal details</FormLabel>
												<Text {...optionalTextStyle}>Optional</Text>
											</HStack>
											<Controller
												name="summary"
												control={control}
												render={({ field }) => (
													<Textarea
														{...field}
														placeholder="Add the details of what you're trying to achieve..."
														rows={4}
														borderRadius="md"
														border="1px solid"
														borderColor="border.default"
														_focus={{ borderColor: 'border.focus', boxShadow: '0 0 0 1px var(--chakra-colors-border-focus)' }}
													/>
												)}
											/>
										</FormControl>
									</MotionBox>

									{/* Advanced Options Content - Show when expanded */}
									<AnimatePresence>
										{isAdvancedOpen && (
											<MotionBox variants={advancedOptionsVariants} initial="hidden" animate="visible" exit="exit" overflow="hidden">
												<VStack spacing={4} mt={0} align="stretch">
													{/* Current Status */}
													<FormControl>
														<HStack {...labelContainerStyle}>
															<FormLabel {...formLabelStyle}>Current status</FormLabel>
														</HStack>
														<Controller
															name="status"
															control={control}
															render={({ field }) => <GoalStatusSelect value={field.value} onChange={field.onChange} />}
														/>
													</FormControl>

													{/* Dates */}
													<Stack spacing={4} align="stretch" direction={{ base: 'column', md: 'row' }}>
														<VStack spacing={0} align="stretch" flex={1}>
															<HStack {...labelContainerStyle}>
																<FormLabel {...formLabelStyle}>Start date</FormLabel>
																<Text {...optionalTextStyle}>Optional</Text>
															</HStack>
															<FormControl>
																<Controller
																	name="startDate"
																	control={control}
																	render={({ field }) => (
																		<DatePicker
																			value={field.value ? parseDateSafely(field.value) : null}
																			onChange={(date) => field.onChange(date?.toISOString())}
																			displayFormat="long"
																			isClearable={false}
																			width="full"
																			strategy="fixed"
																		/>
																	)}
																/>
															</FormControl>
														</VStack>

														<VStack spacing={0} align="stretch" flex={1}>
															<HStack {...labelContainerStyle}>
																<FormLabel {...formLabelStyle}>Target date</FormLabel>
																<Text {...optionalTextStyle}>Optional</Text>
															</HStack>
															<FormControl>
																<Controller
																	name="endDate"
																	control={control}
																	render={({ field }) => (
																		<DatePicker
																			value={field.value ? parseDateSafely(field.value) : null}
																			onChange={(date) => field.onChange(date?.toISOString())}
																			displayFormat="long"
																			isClearable={false}
																			width="full"
																			strategy="fixed"
																		/>
																	)}
																/>
															</FormControl>
														</VStack>
													</Stack>

													{/* Collaborators */}
													<FormControl>
														<HStack {...labelContainerStyle}>
															<FormLabel {...formLabelStyle}>Collaborators</FormLabel>
															<Text {...optionalTextStyle}>Optional</Text>
														</HStack>
														<Controller
															name="collaborators"
															control={control}
															render={({ field }) => (
																<Box px="1px">
																	<CollaboratorSelector
																		value={field.value}
																		onSearch={searchCollaborators}
																		onSelect={(collaborator: Recipient) => {
																			if (!field.value.find((c: Recipient) => c.id === collaborator.id)) {
																				field.onChange([...field.value, collaborator]);
																			}
																		}}
																		onRemove={(collaborator: Recipient) => {
																			field.onChange(field.value.filter((c: Recipient) => c.id !== collaborator.id));
																		}}
																		placeholder="Type to add a collaborator..."
																	/>
																</Box>
															)}
														/>
													</FormControl>

													{/* Initiative - Only show for Organization tier */}
													{isOrganizationTier && (
														<FormControl>
															<HStack {...labelContainerStyle}>
																<FormLabel {...formLabelStyle}>Supporting initiative</FormLabel>
																<Text {...optionalTextStyle}>Optional</Text>
															</HStack>
															<Controller
																name="initiativeId"
																control={control}
																render={({ field }) => {
																	const selectedInitiative = initiatives.find((initiative) => initiative.id === field.value);

																	return (
																		<VStack spacing={3} align="stretch">
																			{!selectedInitiative && (
																				<Box px="1px">
																					<Select
																						{...field}
																						placeholder="Select an initiative..."
																						borderRadius="md"
																						border="1px solid"
																						borderColor="border.default"
																						_focus={{ borderColor: 'border.focus', boxShadow: '0 0 0 1px var(--chakra-colors-border-focus)' }}
																					>
																						{initiatives.map((initiative) => (
																							<option key={initiative.id} value={initiative.id}>
																								{initiative.title}
																							</option>
																						))}
																					</Select>
																				</Box>
																			)}
																			{selectedInitiative && (
																				<GoalInitiativeCard initiative={selectedInitiative} showRemoveButton={true} onRemove={() => field.onChange('')} />
																			)}
																		</VStack>
																	);
																}}
															/>
														</FormControl>
													)}

													{/* Company Values */}
													<FormControl>
														<HStack {...labelContainerStyle} mb={2}>
															<FormLabel {...formLabelStyle}>Which company values does this reflect?</FormLabel>
															<Text {...optionalTextStyle}>Optional</Text>
														</HStack>
														<Controller
															name="tags"
															control={control}
															render={({ field }) => (
																<Wrap>
																	{hashtags?.data?.result?.map((hashtag: string) => {
																		const isSelected = field.value.includes(hashtag);
																		return (
																			<Tag
																				key={hashtag}
																				variant={isSelected ? 'solid' : 'subtle'}
																				size="sm"
																				borderRadius="full"
																				fontWeight="medium"
																				width="fit-content"
																				fontSize="sm"
																				height={8}
																				cursor="pointer"
																				border="1px solid"
																				borderColor="border.default"
																				borderWidth="1px"
																				py={1.5}
																				px={3}
																				color={isSelected ? 'secondary.fgOnSurface' : 'text.default'}
																				bg={isSelected ? 'secondary.bg' : 'bg.surface'}
																				onClick={() => {
																					if (isSelected) {
																						field.onChange(field.value.filter((h: string) => h !== hashtag));
																					} else {
																						field.onChange([...field.value, hashtag]);
																					}
																				}}
																				_hover={{
																					bg: isSelected ? 'secondary.bgHover' : 'secondary.bgAlpha',
																				}}
																			>
																				<HStack spacing={1} align="center">
																					<Text size="sm">{hashtag}</Text>
																					{isSelected && <XOutline size="xs" color="secondary.fgOnSurface" />}
																				</HStack>
																			</Tag>
																		);
																	})}
																</Wrap>
															)}
														/>
														<HStack {...labelContainerStyle} mt={2}>
															<Text {...optionalTextStyle}>Click to select/deselect</Text>
														</HStack>
													</FormControl>
												</VStack>
											</MotionBox>
										)}
									</AnimatePresence>
								</VStack>
							</form>
							{/* Advanced Options Toggle */}
							<Box mt={2}>
								<Button
									leftIcon={isAdvancedOpen ? <ChevronUpOutline /> : <ChevronDownOutline />}
									onClick={onAdvancedToggle}
									variant="ghost"
									size="sm"
									colorScheme="primary"
									fontWeight="medium"
								>
									{isAdvancedOpen ? 'Hide options' : 'Advanced options'}
								</Button>
							</Box>
						</MotionVStack>
					</AnimatePresence>
				</ModalBody>

				<ModalFooter py={3} px={{ base: 4, md: 5 }} bg={bgColor} borderBottomRadius="xl">
					<Button form="create-goal-form" type="submit" colorScheme="primary" isLoading={isSubmitting} loadingText="Creating..." w="full">
						{saveButtonText}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
